/**
 * "Last signed in with X" memory for the auth card.
 *
 * Client-owned localStorage, NOT better-auth's `lastLoginMethod` plugin.
 *
 * This used to be justified by claiming the plugin's server-set cookie would
 * be invisible for social sign-in, because the OAuth callback was "a top-level
 * navigation to the auth origin that the proxy never sees". That is not true:
 * every auth response reaches the browser through the same-origin proxy in
 * both deployments — in prod the callback is registered against the app origin
 * directly, and on previews (where a per-branch origin can't be registered)
 * the worker's oAuthProxy plugin replays to this origin's
 * `/api/auth/oauth-proxy-callback`, which comes back through the proxy too.
 * See server/routes/auth-proxy.ts:94-101 and the comment in client.ts. A
 * domain-less cookie the worker sets therefore lands host-only on the APP
 * origin — first-party, and readable — for social exactly as for email.
 *
 * What actually keeps this file: it needs no server-side change in the
 * separate flashka-auth repo, and flashka-app needs its own SecureStore-backed
 * copy regardless, so the plugin could at best replace the web half. Swapping
 * to it is a live option, not a blocked one — it just has not been worth a
 * cross-repo change for a UI hint.
 *
 * Tradeoff: this is per-browser, so a new device shows no badge until its
 * first sign-in there. The server-cookie approach shares that limitation, so
 * nothing is lost by choosing this one.
 *
 * The auth worker separately records the method on the user row (for admin
 * and support). That is the durable record; this is only a UI hint, and the
 * two are allowed to disagree — signing in on a second device updates the
 * server's copy but not this browser's.
 */

export type LastLoginMethod = 'google' | 'apple' | 'email';

const KEY = 'flashka.ba.last-login-method';

function isMethod(value: string | null): value is LastLoginMethod {
  return value === 'google' || value === 'apple' || value === 'email';
}

/**
 * For social this is called at CLICK time, before the redirect — the session
 * is minted on the callback, so there is no later moment on this origin where
 * the provider is still known. That means an abandoned Google sign-in still
 * records "google". Accepted deliberately: the badge states what the user last
 * reached for, not what last succeeded, and it is a hint rather than a claim.
 * Email flows call it on confirmed success, where the result is known inline.
 */
export function rememberLoginMethod(method: LastLoginMethod): void {
  try {
    localStorage.setItem(KEY, method);
  } catch {
    // Safari private browsing and storage-blocked embeds throw on setItem.
    // The badge is a nicety and must never be able to fail a sign-in.
  }
}

export function getLastLoginMethod(): LastLoginMethod | null {
  try {
    const stored = localStorage.getItem(KEY);
    // Anything unrecognised (a stale value from an older build, a hand-edited
    // entry) reads as "no memory" rather than rendering an unknown label.
    return isMethod(stored) ? stored : null;
  } catch {
    return null;
  }
}

export function clearLastLoginMethod(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // See rememberLoginMethod.
  }
}
